<script setup lang="ts">
import type { CustomElementEvent } from "@lxc/app-device-common";
import { HttpStatusCode } from "axios";
import { storeToRefs } from "pinia";
import { getRuntimeConfig } from "~/config/runtime.config";
import type { Dtwin } from "~/modules/dtwin/models/Dtwin.interface";
import type { DtwinModel } from "~/modules/dtwin/models/DtwinModel.interface";
import type { DynamicUIGroup } from "~/modules/dtwin/models/DynamicUIGrouped.interface";
import { WebComponents } from "~/modules/dtwin/models/WebComponents.enum";
import { useOrganizationStore } from "~/modules/organization/stores/useOrganizationStore";
import { useUserSession } from "~/modules/user/stores/useUserSession";
import { useConfirmLeavePageStore } from "~/stores/useConfirmLeavePageStore";

const props = defineProps<{
  dtwin?: Dtwin | null;
  deviceModelUiConfig?: DynamicUIGroup;
  dtwinModel?: DtwinModel;
}>();

const { userSession } = useUserSession();
const { currentOrganization } = storeToRefs(useOrganizationStore());

const proxyUrl = `${getRuntimeConfig().LXC_AS_DEVICE_URL}/api/v1/proxy`;

/**
 * Load web component if it defined in device model ui config
 */
const loadWebComponent = () => {
  if (props.deviceModelUiConfig?.details?.configuration?.webComponent) {
    const webComponentScript = document.querySelector(
      `[data-web-component=${props.deviceModelUiConfig.details.configuration.webComponent.name}]`,
    );

    if (webComponentScript === null) {
      const script = document.createElement("script");
      script.src = new URL(
        props.deviceModelUiConfig?.details?.configuration?.webComponent
          .url as string,
        window.location.origin,
      ).href;
      script.type = "module";

      script.setAttribute(
        "data-web-component",
        `${props.deviceModelUiConfig.details.configuration.webComponent.name}`,
      );
      document.head.appendChild(script);
    }
  }
};

const removeWebComponent = () => {
  if (props.deviceModelUiConfig?.details?.configuration?.webComponent) {
    const webComponentScript = document.querySelector(
      `[data-web-component=${props.deviceModelUiConfig.details.configuration.webComponent.name}]`,
    );
    if (webComponentScript !== null) {
      document.head.removeChild(webComponentScript);
    }
  }
};

const customElementError = async (error: CustomEvent) => {
  if (error.detail[0].response.status === HttpStatusCode.NotFound) {
    const { logout } = await import("~/utils/auth.utils");
    logout();
  }
};

/**
 * Listen to the Web Component events
 */
const onEvent = (event: CustomEvent) => {
  for (const detail of event.detail) {
    const customElementEvent = detail as CustomElementEvent;

    // case of event "CHANGES": the Web Component has (no) changes on its context => activate/deactivate the confirm leave page message
    if (customElementEvent.type === "CHANGES") {
      if (customElementEvent.args && customElementEvent.args.length > 0) {
        useConfirmLeavePageStore().activate(customElementEvent.args[0]);
      }
    }
  }
};

onMounted(loadWebComponent);
onUnmounted(removeWebComponent);
</script>

<template>
  <v-front-page
    v-if="
      deviceModelUiConfig?.details?.configuration?.webComponent?.name ===
        WebComponents.V_FRONT_PAGE &&
      currentOrganization &&
      currentOrganization.code
    "
    :device-id="dtwin?.uid"
    :proxy-url="proxyUrl"
    :organization-code="currentOrganization.code"
    :lang="userSession?.language"
    @error="customElementError"
    @event="onEvent"
  />
  <dtwin-form-configuration-core
    v-else
    :dtwin="dtwin"
    :dtwin-model="dtwinModel"
    :organization-code="currentOrganization?.code"
    :device-model-ui-config="deviceModelUiConfig"
  />
</template>
